import { Injectable } from '@angular/core';
import { catchError, finalize, Observable, tap, throwError } from 'rxjs';
import { FilterDTO, FilterOperator, ImportConfigurationClient, ImportConfigurationDTO, ImportConfigurationType, SortDirection } from 'src/app/base/models/models.swagger.generated';
import { ToastService } from 'src/app/base/services/toast.service';
import { ShowInactiveService } from 'src/app/base/shared/grid-base/show-inactive.service';
import { IAdaptorService } from 'src/app/base/shared/syncfusion/adaptor-service.interface';
import { IPagedItems } from 'src/app/base/shared/syncfusion/paged-items.interface';
import { SpinnerService } from 'src/app/base/spinner/spinner.service';
import { ErrorType } from 'src/app/core/routing/error-types';

@Injectable({
  providedIn: 'root'
})
export class ImportConfigurationTemplateService extends ShowInactiveService implements IAdaptorService {
  private oneTimeFilter: FilterDTO;

  constructor(
    private importConfigurationClient: ImportConfigurationClient,
    private toastService: ToastService,
    private spinnerService: SpinnerService
    ) {
      super();
  }

  public setOneTimeFilter(filter: FilterDTO) {
    this.oneTimeFilter = filter;
  }

  getData(skip: number, take: number, filters: FilterDTO[], sortBy: string, sortDirection: SortDirection): Observable<IPagedItems> {
    if (!filters) {
      filters = [];
    }
    filters.push(new FilterDTO({ isComplex: false, field: 'type', operator: FilterOperator.Equal, value:  ImportConfigurationType.Template.toString()}));
    if (this.oneTimeFilter) {
      filters.push(this.oneTimeFilter);
    }
    return this.importConfigurationClient.searchImportConfigurations(null, skip, take, sortBy, sortDirection, filters)
      .pipe(
        catchError((error: any) => {
          if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
            this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message', null, ErrorType.serverError);
          }
          return throwError(() => error);
        })
      );
  }

  addRecord(data: ImportConfigurationDTO): Observable<ImportConfigurationDTO> {
    this.spinnerService.show();
    return this.importConfigurationClient.createImportConfigurationTemplate(data).pipe(
      tap(() => {
        this.toastService.showSuccess(
          'i18n.domain.import-configuration.messages.add-title',
          'i18n.domain.import-configuration.messages.add-success-message');
      }),
      finalize(() => {
        this.spinnerService.hide();
      }),
      catchError(err => {
        if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
          this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message', null, ErrorType.serverError);
        }
        return throwError(() => err);
      })
    );
  }

  updateRecord(data: ImportConfigurationDTO): Observable<ImportConfigurationDTO> {
    this.spinnerService.show();
    return this.importConfigurationClient.updateImportConfigurationTemplate(data).pipe(
      tap(() => {
        this.toastService.showSuccess(
          'i18n.domain.import-configuration.messages.update-title',
          'i18n.domain.import-configuration.messages.update-success-message');
      }),
      finalize(() => {
        this.spinnerService.hide();
      }),
      catchError(err => {
        if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
          this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message', null, ErrorType.serverError);
        }
        return throwError(() => err);
      })
    );
  }

  deleteRecord(id: number): Observable<void> {
    return this.importConfigurationClient.deleteImportConfigurationTemplate(id)
    .pipe(
      tap(() => {
        this.toastService.showSuccess(
          'i18n.domain.import-configuration.messages.delete-title',
          'i18n.domain.import-configuration.messages.delete-success-message'
        );
      }),
      catchError((error: any) => {
        if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
          this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message', null, ErrorType.serverError);
        }
        return throwError(() => error);
      })
    );
  }

  getConfigurationById(id: number): Observable<ImportConfigurationDTO> {
    this.spinnerService.show();
    return this.importConfigurationClient.getImportConfiguration(id).pipe(
      tap((config: ImportConfigurationDTO) => {
        config?.fileConfig?.fieldAssignments?.forEach(assignment => {
          if (assignment.fixValue != null) {
            assignment.importFunction = null;
          }
        });
      }),
      finalize(() => {
        this.spinnerService.hide();
      }),
      catchError(err => {
        this.toastService.showError(
          'i18n.errors.server-error.title',
          'i18n.errors.server-error.message', null, ErrorType.serverError);
        return throwError(() => err);
      })
    );
  }

}
